import type { Dispatch, SetStateAction } from "react";

import { initVal } from "~/features/quote/components/quote-details/constants/quote-detail-constants.ts";
import type { SelectedAddressBookItems } from "~/features/quote/context/quote-address-context.tsx";
import { mapQuoteBodyToQuoteState } from "~/features/quote/utils/map-quote-body-to-quote-state.ts";
import type { LocationValue } from "~/features/quote/utils/quote-location-utils.ts";
import { verifyStopAddressBookId } from "~/features/quote/utils/quote-location-utils.ts";
import type { Accessorial } from "~/shared/schemas/accessorial-schema.ts";
import type { AddressBookItemSummary } from "~/shared/schemas/address-book-schema.ts";
import type { FreightClass } from "~/shared/schemas/freight-classes-schema.ts";
import type { QuoteBody } from "~/shared/schemas/quote-schema.ts";
import type { TemplateGridItem } from "~/shared/schemas/template-schema.ts";
import type { UnitType } from "~/shared/schemas/unit-type-schema.ts";
import type { QuoteStateType } from "~/shared/types/quote-details-types.ts";
import { base64Decode } from "~/shared/utils/base64-utils.ts";

type ApplyTemplateToQuoteDetailsArgs = {
	template: TemplateGridItem;
	pageData:
		| {
				freightClassData: FreightClass[];
				unitTypes: UnitType[];
		  }
		| undefined;
	allAccessorials: Accessorial[] | undefined;
	/**
	 * The customer's current saved addresses. The template's stored
	 * addressBookItemId is re-verified against it so stale links (old templates /
	 * changed addresses) do not re-show an edited stop as a saved address. When
	 * unresolved (`undefined`/`null` — still loading or errored) the stored id
	 * cannot be confirmed, so it is detached (id 0) rather than trusted. This
	 * keeps the manual apply path from resurrecting a stale id during the address
	 * book's initial load, since (unlike the default-template and edit-mode flows)
	 * it is a one-shot user action that cannot wait for the query.
	 */
	addressBook?: AddressBookItemSummary[] | null;
	resetSelectedAddressBookItems: () => void;
	setSelectedAddressBookItem: (
		addressType: keyof SelectedAddressBookItems,
		address: AddressBookItemSummary,
	) => void;
	setQuoteState: Dispatch<SetStateAction<QuoteStateType>>;
	onApplied?: () => void;
};

export const applyTemplateToQuoteDetails = ({
	template,
	pageData,
	allAccessorials,
	addressBook,
	resetSelectedAddressBookItems,
	setSelectedAddressBookItem,
	setQuoteState,
	onApplied,
}: ApplyTemplateToQuoteDetailsArgs): boolean => {
	if (!pageData || !allAccessorials || allAccessorials.length === 0) {
		return false;
	}

	try {
		const data = JSON.parse(
			base64Decode(template.raw.templateJson),
		) as Partial<QuoteBody>;
		const { nextState, pickupItem, deliveryItem } = mapQuoteBodyToQuoteState(
			data,
			initVal,
			{
				accessorials: allAccessorials,
				freightClasses: pageData.freightClassData,
				unitTypes: pageData.unitTypes,
			},
		);

		resetSelectedAddressBookItems();
		// Trust the template's stored addressBookItemId only when the stop still
		// exactly matches a current saved address; otherwise detach it (id 0).
		// The manual apply path is a one-shot user action and cannot wait for the
		// address book to load like the default-template and edit-mode flows do, so
		// when the address book is unresolved we detach rather than trust a
		// potentially stale id (which would re-show an edited stop as pristine).
		const detachStaleId = (
			item: AddressBookItemSummary | null,
		): AddressBookItemSummary | null =>
			item && item.addressBookItemId !== 0
				? { ...item, addressBookItemId: 0 }
				: item;
		const verifiedPickup =
			addressBook == null
				? detachStaleId(pickupItem)
				: verifyStopAddressBookId(
						pickupItem,
						data.pickupDetails,
						data.origin as LocationValue | undefined,
						"pickup",
						addressBook,
					);
		const verifiedDelivery =
			addressBook == null
				? detachStaleId(deliveryItem)
				: verifyStopAddressBookId(
						deliveryItem,
						data.deliveryDetails,
						data.destination as LocationValue | undefined,
						"delivery",
						addressBook,
					);
		if (verifiedPickup) {
			setSelectedAddressBookItem("pickup", verifiedPickup);
		}
		if (verifiedDelivery) {
			setSelectedAddressBookItem("delivery", verifiedDelivery);
		}

		setQuoteState((prev) => ({
			...nextState,
			id: prev.id,
			shipmentDate: prev.shipmentDate,
			customer: prev.customer,
			customerDetails: prev.customerDetails,
			customerAr: prev.customerAr,
			customerCredits: prev.customerCredits,
			carrierPrices: prev.carrierPrices,
			selectedCarrierPriceId: prev.selectedCarrierPriceId,
			eTag: prev.eTag,
			createdDate: prev.createdDate,
			expirationDate: prev.expirationDate,
			publicGuid: prev.publicGuid,
			templateId: template.templateId,
			templateName: template.templateName,
			measuringType: prev.measuringType,
		}));

		onApplied?.();
		return true;
	} catch {
		return false;
	}
};
